import { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import { vendorValidations } from '#common/validation';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import useForm from '#utils/use-form';
import { useSaveVendorMutation, useGetActiveVendorsQuery } from '#services/vendor';
import {
  getInitialState,
  useData,
} from './Edit.utils';

interface ActiveVendor {
  name: string;
  [key: string]: unknown;
}

interface RelatedProduct {
  id: string | number;
  name: string;
  vendor?: string;
  [key: string]: unknown;
}

interface ProductChange {
  productId: string | number;
  vendor?: string;
}

export default function VendorEdit() {
  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    isDirty,
    isValid,
    errors,
    errorMessages,
    handleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
  } = useForm({
    initialState: getInitialState(),
    validations: vendorValidations,
  });

  useData({ resetForm });

  const navigate = useNavigate();
  const location = useLocation();
  const [saveVendor] = useSaveVendorMutation();
  const [productChanges, setProductChanges] = useState<ProductChange[]>([]);
  const [filter, setFilter] = useState('');
  const [productPageIndex, setProductPageIndex] = useState(0);
  const productPageSize = 30;

  const { data: activeVendorsData } = useGetActiveVendorsQuery(undefined);
  const activeVendors = (activeVendorsData || []) as ActiveVendor[];

  const isEdit = new URLSearchParams(location.search).has('vendorId')
    || new URLSearchParams(location.search).has('vendorIndex');

  const handleSaveVendor = (values: Record<string, unknown>, isValid: boolean) => {
    if (!isValid || (!isDirty && !productChanges.length)) {
      return;
    }

    showBackdrop();
    const postData: Record<string, unknown> = {};

    if (isDirty) {
      Object.assign(postData, values);
      delete postData.relatedProducts;
    } else {
      postData.id = values.id;
    }

    if (productChanges.length) {
      postData.productChanges = productChanges;
    }

    void saveVendor(postData)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Vendor "${String(values.name)}" was successfully saved.`);

        if (hasError) {
          return;
        }

        if (isEdit) {
          resetForm(getInitialState(res.data));
          setProductChanges([]);
          setFilter('');
        } else {
          void navigate('/management/product/vendor/list');
        }
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveVendor);

  function handleProductVendorChange(productId: string | number, newVendor?: string) {
    const originalVendor = (values.relatedProducts as RelatedProduct[]).find((p) => p.id === productId)?.vendor;
    setProductChanges((prev) => {
      const filtered = prev.filter((pc) => pc.productId !== productId);
      if (newVendor && newVendor !== originalVendor) {
        return [...filtered, { productId, vendor: newVendor }];
      }
      return filtered;
    });
  }

  useEffect(() => {
    setProductPageIndex(0);
  }, [filter, values.id, (values.relatedProducts as RelatedProduct[]).length]);

  let relatedProducts = (values.relatedProducts as RelatedProduct[]).map((rp) => {
    const change = productChanges.find((pc) => pc.productId === rp.id);
    if (!change) return rp;
    return { ...rp, vendor: change.vendor };
  });

  if (filter) {
    relatedProducts = relatedProducts.filter((rp) =>
      rp.name.toLowerCase().includes(filter.toLowerCase().trim()));
  }

  const totalProductCount = relatedProducts.length;
  const paginatedProducts = relatedProducts.slice(
    productPageIndex * productPageSize,
    (productPageIndex + 1) * productPageSize,
  );

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Product',
    renderer: (row) => (
      <Box>
        <Link
          component="button"
          onClick={() => { void navigate(`/management/product/edit?productId=${(row as unknown as RelatedProduct).id}`); }}
        >
          {(row as unknown as RelatedProduct).name}
        </Link>
      </Box>
    ),
  }, {
    id: 'vendor',
    label: 'Vendor',
    sortable: false,
    renderer: (row) => {
      const product = row as unknown as RelatedProduct;
      return (
        <Autocomplete
          size="small"
          sx={{ width: 220 }}
          options={activeVendors}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          isOptionEqualToValue={(option, value) => option.name === (typeof value === 'string' ? value : value?.name)}
          value={activeVendors.find((v) => v.name === product.vendor) || null}
          onChange={(event, value) => handleProductVendorChange(product.id, value?.name)}
          renderInput={(params) => <TextField {...params} size="small" />}
        />
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'products',
    headers: tableHeaders,
    enableSort: false,
    rowCount: totalProductCount,
    rows: paginatedProducts,
    pageIndex: productPageIndex,
    pageSize: productPageSize,
    setPageIndex: setProductPageIndex,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        {isEdit ? 'Edit' : 'Add'}
        {' '}
        Vendor
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: 2, pb: 1, mb: 1 }}
      >
        <TextField
          required
          fullWidth
          size="small"
          name="name"
          label="Name"
          value={values.name}
          error={!!errors.name}
          helperText={errorMessages.name as string}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          size="small"
          name="description"
          label="Description"
          value={values.description}
          error={!!errors.description}
          helperText={errorMessages.description as string}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          multiline
          rows={5}
          size="small"
          name="notes"
          label="Notes"
          value={values.notes}
          error={!!errors.notes}
          helperText={errorMessages.notes as string}
          onChange={handleChange}
        />
      </Paper>

      {isEdit && (
        <>
          <Box sx={{ typography: 'h7' }}>
            Products
          </Box>

          <Paper
            elevation={0}
            variant="outlined"
            square
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: 400,
            }}
          >
            <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Filter Products"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </Paper>

          <EnhancedTable {...tableProps} />
        </>
      )}

      <Button
        variant="outlined"
        disabled={!isValid || (!isDirty && !productChanges.length)}
        onClick={handleSubmit}
      >
        Save
      </Button>
    </>
  );
}
